import { useMemo } from "react";
import type { useInboxConversation } from "@tellescope/tanstack-query";
import { useInboxThreadSearchParams } from "./use-inbox-thread-search-params";

type InboxConversationData = ReturnType<typeof useInboxConversation>;

export interface UseInboxThreadDraftArgs {
  conversationData: Pick<InboxConversationData, "messages" | "drafts" | "scheduled" | "isLoading">;
}

/**
 * Shared draft / scheduled-message editing derivation for inbox thread composition footers.
 * Resolves the draft referenced by the `draftId` search param and whether it is a scheduled
 * message being edited. Footers run their own model-typed `getOne` query gated on `shouldQueryDraft`.
 */
export const useInboxThreadDraft = ({ conversationData }: UseInboxThreadDraftArgs) => {
  const [queryParams, setQueryParams] = useInboxThreadSearchParams();
  const { messages, drafts, scheduled, isLoading } = conversationData;

  const matchingDraft = useMemo(
    () =>
      drafts.find(({ id }) => id === queryParams.draftId) ??
      scheduled.find(({ id }) => id === queryParams.draftId) ??
      messages.find(({ id }) => id === queryParams.draftId),
    [drafts, messages, queryParams.draftId, scheduled]
  );

  const isEditingScheduled = useMemo(
    () => !!matchingDraft && scheduled.some(({ id }) => id === matchingDraft.id),
    [matchingDraft, scheduled]
  );

  const sendAtInPast = useMemo(
    () => isEditingScheduled && !!matchingDraft?.sendAt && matchingDraft.sendAt.getTime() <= Date.now(),
    [isEditingScheduled, matchingDraft]
  );

  const shouldQueryDraft = !!queryParams.draftId && !matchingDraft && !isLoading;

  return { queryParams, setQueryParams, matchingDraft, isEditingScheduled, sendAtInPast, shouldQueryDraft };
};
